import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import { QUERIES, showError } from '_metronic/helpers'
import { getNotificacaoById } from '../../core/_requests'
import { useNotificacoes } from '../../core/NotificacoesProvider'

/**
 * Abre o detalhe da notificação indicada em `?id=` — é por onde chega o clique na notificação
 * push (o service worker abre `/apps/notificacoes?id=<notificacaoId>`). O parâmetro é removido
 * da URL logo em seguida, para que recarregar a página não reabra o modal.
 *
 * Busca antes de abrir para ter o título e a referência; `getNotificacaoById` também marca como
 * lida. Daí em diante segue o mesmo despacho do "Ver notificação" da lista.
 */
export const useAbrirNotificacaoPelaUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { abrirNotificacao } = useNotificacoes()
  const queryClient = useQueryClient()

  const idParam = searchParams.get('id')
  // O efeito reexecuta sem `id` novo — StrictMode (dev) roda duas vezes antes de a remoção do
  // `?id=` refletir na URL, e `searchParams`/`setSearchParams` mudam a cada navegação. Esta guarda
  // faz dessas reexecuções um no-op: só a chegada de um novo `id` abre o modal.
  const ultimoIdTratado = useRef<string>()

  useEffect(() => {
    if (!idParam) {
      ultimoIdTratado.current = undefined
      return
    }
    if (ultimoIdTratado.current === idParam) return
    ultimoIdTratado.current = idParam

    const proximos = new URLSearchParams(searchParams)
    proximos.delete('id')
    setSearchParams(proximos, { replace: true })

    const id = Number(idParam)
    if (!Number.isInteger(id) || id <= 0) return

    const invalidar = () => {
      queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
      queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT])
    }

    getNotificacaoById(id)
      .then((notificacao) => {
        invalidar()
        return abrirNotificacao(notificacao, { view: notificacao })
      })
      .catch(showError)
  }, [idParam, searchParams, setSearchParams, abrirNotificacao, queryClient])
}
